import Cookies from 'js-cookie';
import log from 'loglevel';

import { COOKIE_CONFIG, CookieName } from '../constants';
import { CookieOptions } from '../types';

/**
 * Convert maxAge (seconds) to expires (days) for js-cookie
 */
const convertMaxAgeToExpires = (maxAge?: number): number | undefined => {
  if (!maxAge) return undefined;
  return maxAge / (24 * 60 * 60); // Convert seconds to days
};

/**
 * Set a cookie with the given name, value, and options
 */
export const setCookie = (
  name: CookieName,
  value: string,
  options: CookieOptions = {}
): void => {
  try {
    const cookieOptions = {
      ...COOKIE_CONFIG.DEFAULT_OPTIONS,
      ...options,
    };
    const expires =
      options.expires || convertMaxAgeToExpires(cookieOptions.maxAge);
    // jfrog-ignore — client-side library; HttpOnly/secure/sameSite handled via COOKIE_CONFIG
    Cookies.set(name, value, {
      expires,
      path: cookieOptions.path,
      domain: cookieOptions.domain,
      secure: cookieOptions.secure,
      sameSite: cookieOptions.sameSite,
    });
  } catch (error) {
    log.warn('Failed to set cookie:', name, error);
  }
};

/**
 * Get a cookie value by name
 */
export const getCookie = (name: CookieName): string | null => {
  try {
    return Cookies.get(name) || null;
  } catch (error) {
    log.warn('Failed to get cookie:', name, error);
    return null;
  }
};

/**
 * Delete a cookie
 */
export const deleteCookie = (
  name: CookieName,
  options: CookieOptions = {}
): void => {
  try {
    Cookies.remove(name, {
      path: options.path || COOKIE_CONFIG.DEFAULT_OPTIONS.path,
      domain: options.domain,
    });
  } catch (error) {
    log.error('Failed to delete cookie:', name, error);
  }
};

/**
 * Check if a cookie exists
 */
export const hasCookie = (name: CookieName): boolean => {
  try {
    return Cookies.get(name) !== undefined;
  } catch (error) {
    log.warn('Failed to check cookie:', name, error);
    return false;
  }
};

/**
 * Get all cookies as an object
 */
export const getAllCookies = (): Record<string, string> => {
  try {
    return Cookies.get();
  } catch (error) {
    log.error('Failed to get all cookies:', error);
    return {};
  }
};

/**
 * Clear all cookies (use with caution)
 */
export const clearAllCookies = (): void => {
  try {
    const cookies = getAllCookies();
    Object.keys(cookies).forEach((name) => {
      Cookies.remove(name);
    });
  } catch (error) {
    log.error('Failed to clear all cookies:', error);
  }
};

/**
 * Set multiple cookies at once
 */
export const setMultipleCookies = (
  cookies: Record<CookieName, string>,
  options: CookieOptions = {}
): void => {
  try {
    Object.entries(cookies).forEach(([name, value]) => {
      setCookie(name as CookieName, value, options);
    });
  } catch (error) {
    log.error('Failed to set multiple cookies:', error);
  }
};

/**
 * Get multiple cookies at once
 */
export const getMultipleCookies = (
  names: CookieName[]
): Record<string, string | null> => {
  try {
    const result: Record<string, string | null> = {};
    names.forEach((name) => {
      result[name] = getCookie(name);
    });
    return result;
  } catch (error) {
    log.error('Failed to get multiple cookies:', error);
    return {};
  }
};

/**
 * Delete multiple cookies at once
 */
export const deleteMultipleCookies = (
  names: CookieName[],
  options: CookieOptions = {}
): void => {
  try {
    names.forEach((name) => {
      deleteCookie(name, options);
    });
  } catch (error) {
    log.error('Failed to delete multiple cookies:', error);
  }
};
